import { useState } from 'react'

import { Button, DialogFooter, LoadingButton } from '@fortem-monorepo/react-ui'
import { getErrorMessage } from '@fortem-monorepo/web-sdk'

import { useCreateStrategyMutation } from '@/react-query/mutations/strategies'
import { useCurrencies } from '@/react-query/queries/currencies'
import { useStrategyOptions } from '@/react-query/queries/strategies'

import { CreateStrategyForm } from '../forms/CreateStrategyForm/CreateStrategyForm'
import type { CreateStrategyValues } from '../forms/CreateStrategyForm/CreateStrategyForm.schema'
import { CreateStrategyFormFields } from '../forms/CreateStrategyForm/CreateStrategyFormFields'
import { toCreateStrategyParams } from '../forms/strategy-params'
import { StrategyDialogFrame } from './StrategyDialogFrame'
import { StrategyOptionsFeedback } from './StrategyOptionsFeedback'
import { StrategyReviewTable } from './StrategyReviewTable'

export type CreateStrategyDialogProps = { onClose: () => void }
type Review = { values: CreateStrategyValues; currency: string }

const CREATE_STRATEGY_DEFAULTS = {
  strategy_name: '',
  strategy_type: '',
  currency_id: '',
  is_internal: 'false',
  active: 'true',
} as const satisfies CreateStrategyValues

export const CreateStrategyDialog = ({ onClose }: CreateStrategyDialogProps) => {
  const currenciesQuery = useCurrencies()
  const optionsQuery = useStrategyOptions()
  const mutation = useCreateStrategyMutation()
  const [review, setReview] = useState<Review | null>(null)

  const canReview = currenciesQuery.isSuccess && optionsQuery.isSuccess && !mutation.isPending

  const confirm = async () => {
    if (!review || mutation.isPending) {
      return
    }

    const params = toCreateStrategyParams(review.values)

    try {
      await mutation.mutateAsync(params)

      onClose()
    } catch {
      // The hook reports the error; preserve the review and input.
    }
  }

  return (
    <StrategyDialogFrame
      title={review ? 'Confirm strategy creation' : 'Create strategy'}
      description={
        review ? 'Review the details and confirm to save.' : 'Enter the strategy details, then review before saving.'
      }
      pending={mutation.isPending}
      onClose={onClose}
    >
      <StrategyOptionsFeedback query={currenciesQuery} label="currencies" />
      <StrategyOptionsFeedback query={optionsQuery} label="strategy types" />
      <CreateStrategyForm
        strategyTypes={optionsQuery.data?.strategy_types ?? []}
        defaultValues={CREATE_STRATEGY_DEFAULTS}
        onSubmit={(values) => {
          if (review || !canReview) {
            return
          }

          setReview({
            values: { ...values },
            currency:
              currenciesQuery.data?.find((currency) => String(currency.value) === values.currency_id)?.label ?? '',
          })
        }}
      >
        {({ formState }, submit) => (
          <>
            <div hidden={!!review}>
              <CreateStrategyFormFields
                strategyTypes={optionsQuery.data?.strategy_types ?? []}
                currencies={currenciesQuery.data ?? []}
                disabled={!canReview || !!review}
              />
            </div>
            {review && (
              <StrategyReviewTable
                headers={['Field', 'Value']}
                rows={[
                  ['Strategy name', review.values.strategy_name],
                  ['Strategy type', review.values.strategy_type],
                  ['Currency', review.currency],
                  ['Management', review.values.is_internal === 'true' ? 'INTERNAL' : 'EXTERNAL'],
                  ['Status', review.values.active === 'true' ? 'ACTIVE' : 'INACTIVE'],
                ]}
              />
            )}

            {mutation.isError && (
              <p role="alert" className="text-sm text-destructive my-3">
                {getErrorMessage(mutation.error)}
              </p>
            )}
            <DialogFooter className="mt-4">
              <Button type="button" size="sm" variant="outline" disabled={mutation.isPending} onClick={onClose}>
                Cancel
              </Button>
              {review ? (
                <>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={mutation.isPending}
                    onClick={() => setReview(null)}
                  >
                    Back
                  </Button>
                  <LoadingButton type="button" size="sm" disabled={mutation.isPending} onClick={confirm}>
                    Confirm creation
                  </LoadingButton>
                </>
              ) : (
                <LoadingButton
                  type="submit"
                  size="sm"
                  disabled={!formState.isValid || formState.isSubmitting || !canReview}
                  onClick={async (event) => {
                    event.preventDefault()
                    await submit()
                  }}
                >
                  Review creation
                </LoadingButton>
              )}
            </DialogFooter>
          </>
        )}
      </CreateStrategyForm>
    </StrategyDialogFrame>
  )
}
